import React from 'react';
import { Box } from '@mui/material';
import { useLabBasket } from '../../contexts/LabBasketContext';
import { LABOKLIN_RED } from '../../theme/laboklinTheme';

/**
 * A Laboklin service the assistant offered as the test that would settle a question,
 * rendered as an add-to-order control so the veterinarian can pick it up in the
 * sentence that argues for it rather than copying a number into an order form
 * afterwards.
 *
 * This subscribes to the basket itself rather than taking it as a prop, and that is
 * the point of it being a component at all. The chat's link renderer is memoized on
 * the URN click handler, so a basket passed down through it is captured on first
 * render and never refreshed - `add` still worked, being stable, but `has` was
 * permanently the empty-basket one, so a test stayed outlined with a plus after it
 * had been added. Reading the basket at the leaf keeps the control honest however
 * the renderer above it is memoized.
 */
interface ServiceOrderButtonProps {
  serviceNo: number;
  /** What the assistant called it, with no service number and no price. */
  name: string;
  /** Euros, or null when the assistant linked a service without one. */
  price: number | null;
}

const ServiceOrderButton: React.FC<ServiceOrderButtonProps> = ({ serviceNo, name, price }) => {
  const basket = useLabBasket();
  const display = price !== null ? `${name} · €${price.toFixed(2)}` : name;

  if (!basket) {
    // No laboratory basket in this surface - the GekkoVet app - so the test reads as
    // ordinary text rather than an affordance that does nothing.
    return <>{display}</>;
  }

  const inBasket = basket.has(serviceNo);

  return (
    <Box
      component="button"
      type="button"
      aria-label={inBasket ? `${name}, added to order` : `Add ${name} to order`}
      aria-pressed={inBasket}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        if (inBasket) basket.remove(serviceNo);
        else basket.add({ serviceNo, name, price });
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        mx: 0.25,
        fontSize: '0.85em',
        fontWeight: 500,
        lineHeight: 1.6,
        cursor: 'pointer',
        borderRadius: 1,
        border: '1px solid',
        transition: 'background-color 120ms, color 120ms',
        // Laboklin's own red throughout: this is an ordering control in their portal,
        // not a GekkoVet action. Outlined until the test is in the order, filled once
        // it is, so what has been picked up reads at a glance.
        borderColor: LABOKLIN_RED,
        bgcolor: inBasket ? LABOKLIN_RED : 'transparent',
        color: inBasket ? 'common.white' : LABOKLIN_RED,
        '&:hover': {
          bgcolor: inBasket ? LABOKLIN_RED : 'rgba(229, 30, 30, 0.08)',
        },
      }}
    >
      <Box component="span" sx={{ fontWeight: 700, lineHeight: 1 }}>
        {inBasket ? '✓' : '+'}
      </Box>
      {display}
    </Box>
  );
};

export default ServiceOrderButton;
